import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';

import openInsightModal from 'components/partials/insight-modal/insight-modal';

import { setPendingInsightImage } from 'components/partials/insight-modal/insight-context';

import {
  hashBlob,
  isSafari,
  pollUntil,
  tryReadClipboardImage,
  tryReadClipboardImageSafari,
  waitForVisibility,
} from 'helpers/utils/screenshot-core';
import SnappingBar from 'helpers/utils/components/snapping-bar';
import type { SnappingRect } from 'helpers/utils/components/snapping-bar';
import DimMasks from 'helpers/utils/components/dim-masks';

interface CaptureAbort {
  isAborted: () => boolean;
  abort: () => void;
  bind: () => void;
}

interface CaptureMetadata {
  isReport?: boolean;
  reportName?: string;
  title?: string;
  [key: string]: unknown;
}

interface ShowImageModalOptions {
  targetElement?: HTMLElement | null;
  metadata?: CaptureMetadata;
}

let activeCaptureAbort: (() => void) | null = null;
const cancelActiveCapture = () => {
  if (typeof activeCaptureAbort === 'function') {
    activeCaptureAbort();
  }
};

const createCaptureAbort = (): CaptureAbort => {
  let aborted = false;

  const abort = () => {
    aborted = true;
    window.removeEventListener('beforeunload', abort);
    activeCaptureAbort = null;
  };

  const bind = () => {
    window.addEventListener('beforeunload', abort, { once: true });
    activeCaptureAbort = abort;
  };

  return { isAborted: () => aborted, abort, bind };
};

const finalizeCapture = (abort: () => void) => {
  hideFocusOverlay();
  window.removeEventListener('beforeunload', abort);
  activeCaptureAbort = null;
};

interface FocusOverlayProps {
  targetElement: HTMLElement | null | undefined;
  onPasteClick?: ((event: React.MouseEvent<HTMLButtonElement>) => void) | null;
}

const EMPTY_RECT: SnappingRect = {
  top: 0,
  left: 0,
  width: 0,
  height: 0,
  right: 0,
  bottom: 0,
};

const rectFromDom = (r: DOMRect | null | undefined): SnappingRect =>
  r
    ? {
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
        right: r.right,
        bottom: r.bottom,
      }
    : EMPTY_RECT;

const FocusOverlay: React.FC<FocusOverlayProps> = ({
  targetElement,
  onPasteClick,
}) => {
  const [rect, setRect] = useState<SnappingRect>(() =>
    rectFromDom(targetElement?.getBoundingClientRect())
  );

  useEffect(() => {
    const update = () => {
      const r = targetElement?.getBoundingClientRect();

      if (r) {
        setRect(rectFromDom(r));
      }
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [targetElement]);

  return (
    <>
      <SnappingBar
        rect={rect}
        onCancel={(e) => {
          (e as React.MouseEvent | undefined)?.stopPropagation?.();
          cancelActiveCapture();
          hideFocusOverlay();
        }}
        onPasteClick={onPasteClick}
      />
      <DimMasks rect={rect} />
    </>
  );
};

let focusOverlayRoot: Root | null = null;
const showFocusOverlay = (
  targetElement: HTMLElement | null | undefined,
  onPasteClick:
    | ((event: React.MouseEvent<HTMLButtonElement>) => void)
    | null = null
): void => {
  const container = document.getElementById('page-overlay') || document.body;

  if (!focusOverlayRoot) {
    focusOverlayRoot = createRoot(container);
  }
  focusOverlayRoot.render(
    <FocusOverlay targetElement={targetElement} onPasteClick={onPasteClick} />
  );
};

const hideFocusOverlay = (): void => {
  if (focusOverlayRoot) {
    focusOverlayRoot.render(<></>);
    focusOverlayRoot = null;
  }
};

const showImageModal = (
  blob: Blob,
  options: ShowImageModalOptions = {}
): void => {
  const url = URL.createObjectURL(blob);
  const { metadata } = options;

  setPendingInsightImage({ blob, url });
  openInsightModal({ metadata });
};

export const openScreenshotCapture = (
  targetElement?: HTMLElement | null,
  metadata: CaptureMetadata | undefined = undefined
): void => {
  const safariMode = isSafari();

  if (safariMode) {
    return openScreenshotCaptureSafari(targetElement, metadata);
  }

  const begin = async () => {
    if (targetElement) showFocusOverlay(targetElement);
    if (document.visibilityState !== 'visible') await waitForVisibility();

    let baselineHash: string | null = null;
    const baselineBlob = await tryReadClipboardImage();

    if (baselineBlob) {
      baselineHash = await hashBlob(baselineBlob);
    }

    const { isAborted, abort, bind } = createCaptureAbort();

    bind();

    const newBlob = await pollUntil<Blob>(
      async () => {
        const blob = await tryReadClipboardImage();

        if (!blob) return null;
        const currentHash = await hashBlob(blob);

        if (!baselineHash || currentHash !== baselineHash) return blob;

        return null;
      },
      { intervalMs: 700, isAborted: () => isAborted() }
    );

    if (newBlob) {
      finalizeCapture(abort);
      showImageModal(newBlob, { targetElement, metadata });

      return;
    }

    finalizeCapture(abort);
  };

  begin();
};

const openScreenshotCaptureSafari = (
  targetElement?: HTMLElement | null,
  metadata: CaptureMetadata | undefined = undefined
): void => {
  const begin = async () => {
    if (document.visibilityState !== 'visible') await waitForVisibility();

    const { abort, bind } = createCaptureAbort();

    bind();

    const handlePasteClick = async (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      const buttonElement = event.currentTarget;

      try {
        const blob = await tryReadClipboardImageSafari(buttonElement);

        if (blob) {
          finalizeCapture(abort);
          showImageModal(blob, { targetElement, metadata });
        } else {
          console.warn('No image found in clipboard');
        }
      } catch (error) {
        console.error('Failed to paste screenshot:', error);
      }
    };

    if (targetElement) {
      showFocusOverlay(targetElement, handlePasteClick);
    }
  };

  begin();
};

export default openScreenshotCapture;
